import {
  FormImage,
  NewImage,
} from "src/store/useUploadProduct/FormImagesSlice/formImagesTypes";
import {
  ImageInfo,
  SignedUrlBodyRequestData,
  SignedUrlResponse,
} from "./model/upload-image.model";
import { OpenapiSingleton } from "../OpenapiSingleton";
import { useShop } from "src/hooks/Utils/Shop/useShop";
import { getApiResellHost, isLocal } from "src/utils/BasePathTools";
import { getToken } from "src/utils/TokenTools";
import { getOvixiaTokenFromCookie } from "../utils";
import {
  logNewImageUploadError,
  logNewImageUploadSuccess,
  logOriginalImageUploadRequest,
  logOriginalImageUploadResult,
} from "src/utils/observability/logs/product-creation/productCreationLogs";

const RETRY_DELAY = 500;
const RETRY_NUMBER = 4;
const DELAY_POOLING = 1000;
const APPLICATION_NAME = "Zara Pre-owned Resell";

interface ImageData {
  contentLength: number;
  assetId: string;
  index: number;
  indexFile: number;
}

export interface FilesData {
  image: FormImage;
  index: number;
}

export const UploadImageErrorTypes = {
  Signing: "SIGNING",
  Uploading: "Uploading",
  Pooling: "POOLING",
  InvalidContentType: "invalid-content-type",
  EmptyFile: "empty-file",
} as const;
export type UploadImageErrorTypes =
  (typeof UploadImageErrorTypes)[keyof typeof UploadImageErrorTypes];

export async function uploadImagesArray(
  files: FilesData[],
  serviceAccount: string,
  isOriginalImageEndPointActive = false,
): Promise<
  { deliveryUrl?: string; assetId?: String; reason?: any; indexFile?: number }[]
> {
  const filesArray = Array.from(files);
  const uploadableFiles = filesArray.filter((file) =>
    isImageUploadable((file?.image?.content as NewImage).file),
  );
  const uploadPromises = uploadableFiles.map((file, index) => {
    const originalFile = (file.image?.content as NewImage)?.originalFile;
    return uploadImage(
      (file.image?.content as NewImage).file,
      file.index,
      index,
      serviceAccount,
      originalFile,
      isOriginalImageEndPointActive,
    );
  });
  const sortedImages = Promise.all(uploadPromises)
    .then((images) =>
      images.sort((img1, img2) => img2?.contentLength - img1?.contentLength),
    )
    .catch((reason) => [reason]);
  await delay(DELAY_POOLING);
  let poolingImages = [];
  for (const image of await sortedImages) {
    const images = await getPoolingImages(image);
    if (images) {
      const newPoolingImages = [...poolingImages];
      newPoolingImages[image?.index] = images;
      poolingImages = newPoolingImages;
    } else break;
  }
  const response = await Promise.all(poolingImages).catch((reason) => [reason]);
  return response;
}

function isImageUploadable(file: File) {
  const mimeType = getMimeType(file);
  return (
    mimeType === "image/jpeg" ||
    mimeType === "image/jpg" ||
    mimeType === "image/png"
  );
}

function isImageUploadableForNewEndpoint(file: File) {
  const mimeType = getMimeType(file);
  return (
    mimeType === "image/jpeg" ||
    mimeType === "image/jpg" ||
    mimeType === "image/png" ||
    mimeType === "image/webp" ||
    mimeType === "image/gif"
  );
}

export async function createGetSignedUrlBody(
  imageInfo: ImageInfo,
): Promise<SignedUrlBodyRequestData> {
  const { file, contentType, contentLength, serviceAccount } = imageInfo;
  const originalAssetName = file.name;
  return {
    optimize: false,
    application: APPLICATION_NAME,
    metadata: {
      partnumber: "1234567890",
      brand: "Zara",
    },
    contentType,
    contentLength,
    originalAssetName,
    serviceAccount,
    visibility: "PUBLIC",
    tenant: "ZARA",
  };
}

export function getUploadHeaders(
  imageInfo: ImageInfo,
  signedUrlResponse: SignedUrlResponse,
): Record<string, string> {
  const { url, assetId } = signedUrlResponse;
  const { file, contentType, contentLength, serviceAccount } = imageInfo;
  const headers = {
    "Content-Type": contentType,
    "x-goog-content-length-range": `${contentLength},${contentLength}`,
    "x-goog-meta-sa": serviceAccount,
    "x-goog-meta-application": APPLICATION_NAME,
    "x-goog-meta-version": "2",
    "x-goog-meta-assetid": assetId,
    "x-goog-meta-originalassetname": file?.name,
    "x-goog-meta-tenant": "ZARA",
    "x-goog-meta-visibility": "PUBLIC",
    googleSignedUrl: url,
  };
  return headers;
}

export async function uploadImage(
  file: File,
  indexFile: number,
  index: number,
  serviceAccount: string,
  originalFile?: File,
  isOriginalImageEndPointActive = false,
): Promise<{
  contentLength: number;
  assetId: string;
  index: number;
  indexFile: number;
}> {
  const imagePartialInfo = await getImageInfo(file, indexFile, index);
  const imageInfo = { ...imagePartialInfo, serviceAccount };

  const signedUrlResponse =
    await OpenapiSingleton.AmandaApiInstance.signedURL(imageInfo);

  // Launch original image upload in parallel with Amanda PUT (do not block on original)
  fireAndForgetOriginalImageUpload(
    originalFile,
    signedUrlResponse.assetId,
    index,
    isOriginalImageEndPointActive,
  );

  const { assetId } = await OpenapiSingleton.AmandaApiInstance.uploadSignedURL(
    imageInfo,
    signedUrlResponse,
  );
  const { contentLength } = imageInfo;
  return { contentLength, assetId, index, indexFile };
}

async function getPoolingImages(
  image: ImageData,
): Promise<{ deliveryUrl: string; assetId: string; indexFile: number }> {
  const { assetId, indexFile } = image;
  const deliveryUrl = await poolingForDeliveryUrl(assetId);
  if (deliveryUrl) {
    return { deliveryUrl, assetId, indexFile };
  } else {
    return await Promise.reject(new Error(UploadImageErrorTypes.Pooling));
  }
}

async function getImageInfo(
  file: File,
  indexFile: number,
  uploadSequence: number,
): Promise<ImageInfo> {
  const contentType = getMimeType(file);
  const contentLength = file.size;
  // const { serviceAccount } = getEnv();
  return { file, contentType, contentLength, indexFile, uploadSequence };
}

function getMimeType(file: File) {
  return file?.type;
}

function delay(t: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(), t);
  });
}
async function poolingForDeliveryUrl(assetId: string): Promise<string> {
  let deliveryURL = "";
  let counter = 0;
  while (!deliveryURL && counter < RETRY_NUMBER) {
    await delay(RETRY_DELAY);
    deliveryURL = await getDeliveryUrl(assetId);
    counter++;
  }
  return deliveryURL;
}

export async function getDeliveryUrl(assetId: string): Promise<string> {
  const { shopLang, shopMarketId } = useShop();

  try {
    const response =
      await OpenapiSingleton.AmandaServiceManagementAPIApi.getImageDataV2(
        assetId,
        shopMarketId,
        shopLang,
      ).catch((err) => "");

    if (typeof response === "string") {
      return "";
    } else {
      return response.data?.deliveryURL;
    }
  } catch (error) {
    return "";
  }
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface OriginalImageUploadResponse {
  status: number;
  statusText: string;
  responseBody: string;
  responseType: string;
  akamaiRef: string;
}

interface PresignedUploadHeaders {
  "Content-Type": string;
  "x-amz-tagging": string;
  "x-amz-meta-sub": string;
  "x-amz-meta-iss": string;
  "x-amz-meta-version": string;
  "x-amz-meta-authtype": string;
  [key: string]: string;
}

interface PresignedUploadData {
  url: string;
  key: string;
  bucket: string;
  content_type: string;
  expires_in: number;
  headers: PresignedUploadHeaders;
  tags: Record<string, string>;
}

interface PresignedUrlApiResponse {
  status: string;
  upload: PresignedUploadData;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fireAndForgetOriginalImageUpload(
  originalFile: File | undefined,
  assetId: string,
  index: number,
  isOriginalImageEndPointActive: boolean,
): void {
  if (!isOriginalImageEndPointActive || !originalFile) return;

  logOriginalImageUploadRequest(originalFile, assetId, index);

  void uploadOriginalImage(originalFile, assetId)
    .then((result) =>
      logOriginalImageUploadResult(originalFile, assetId, index, true, undefined, {
        responseStatus: result.status,
        responseStatusText: result.statusText,
        responseBody: result.responseBody,
        responseType: result.responseType,
        akamaiRef: result.akamaiRef,
      }),
    )
    .catch((error) => {
      const ur = (error as any)?.uploadResponse;
      logOriginalImageUploadResult(
        originalFile,
        assetId,
        index,
        false,
        error,
        ur
          ? {
              responseStatus: ur.status,
              responseStatusText: ur.statusText,
              responseBody: ur.responseBody,
              responseType: ur.responseType,
              akamaiRef: ur.akamaiRef,
            }
          : undefined,
      );
      console.error(
        `[fireAndForgetOriginalImageUpload] Failed to upload original image for assetId ${assetId}:`,
        error,
      );
    });
}

function buildAuthHeaders(): Record<string, string> {
  if (isLocal) {
    return { Authorization: "Bearer " + getToken()?.token };
  }
  const ovixiaToken = getOvixiaTokenFromCookie();
  if (ovixiaToken) {
    return { Authorization: "Bearer " + ovixiaToken };
  }
  return {};
}

async function readResponseBodySafe(response: Response): Promise<string> {
  try {
    const body = await response.text();
    return body.length > 500 ? body.substring(0, 500) + "...[truncated]" : body;
  } catch (_) {
    return "[unable to read body]";
  }
}

function buildUploadErrorResponse(
  response: Response,
  responseBody: string,
  statusText?: string,
): OriginalImageUploadResponse {
  return {
    status: response.status,
    statusText: statusText ?? response.statusText,
    responseBody,
    responseType: response.type,
    akamaiRef: "",
  };
}

// ─── Steps ───────────────────────────────────────────────────────────────────

async function requestPresignedUrl(
  endpoint: string,
  originalFile: File,
  assetId: string,
  authHeaders: Record<string, string>,
): Promise<PresignedUrlApiResponse> {
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify({
      filename: originalFile.name,
      contentType: originalFile.type,
      "asset-id": assetId,
      "upload-source": "spa",
    }),
  });

  if (!response.ok) {
    const responseBody = await readResponseBodySafe(response);
    throw Object.assign(
      new Error(`HTTP ${response.status} ${response.statusText}`),
      { uploadResponse: buildUploadErrorResponse(response, responseBody) },
    );
  }

  return response.json() as Promise<PresignedUrlApiResponse>;
}

async function uploadFileToS3(
  upload: PresignedUploadData,
  file: File,
): Promise<OriginalImageUploadResponse> {
  const response = await fetch(upload.url, {
    method: "PUT",
    headers: upload.headers,
    body: file,
  });

  const responseBody = await readResponseBodySafe(response);
  const result: OriginalImageUploadResponse = {
    status: response.status,
    statusText: response.statusText,
    responseBody,
    responseType: response.type,
    akamaiRef: "",
  };

  if (!response.ok) {
    throw Object.assign(
      new Error(`HTTP ${response.status} ${response.statusText}`),
      { uploadResponse: result },
    );
  }

  return result;
}

// ─── Public API ──────────────────────────────────────────────────────────────

export async function uploadOriginalImage(
  originalFile: File,
  assetId: string,
): Promise<OriginalImageUploadResponse> {
  const endpoint = `${getApiResellHost()}/api/preowned/images/upload`;
  const authHeaders = buildAuthHeaders();
  const presignData = await requestPresignedUrl(
    endpoint,
    originalFile,
    assetId,
    authHeaders,
  );
  return uploadFileToS3(presignData.upload, originalFile);
}

export async function uploadNewImage(
  file: File,
  indexFile: number,
  storeId: number,
  lang?: string,
  originalFile?: File,
  isOriginalImageEndPointActive = false,
): Promise<{ deliveryUrl: string; assetId: string; indexFile: number }> {
  try {
    const result = await OpenapiSingleton.SellerProductAPIApi.uploadProductImage(
      storeId,
      file,
      lang,
    );
    fireAndForgetOriginalImageUpload(
      originalFile,
      result.data.assetId,
      indexFile,
      isOriginalImageEndPointActive,
    );
    logNewImageUploadSuccess(indexFile, result.data.assetId, result.data.deliveryUrl);
    return {
      assetId: result.data.assetId,
      deliveryUrl: result.data.deliveryUrl,
      indexFile,
    };
  } catch (error: any) {
    logNewImageUploadError(indexFile, error, file);
    const title = error?.response?.data?.title;
    throw new Error(title ?? "UNKNOWN_ERROR");
  }
}

export async function uploadNewImagesArray(
  files: FilesData[],
  storeId: number,
  lang?: string,
  isOriginalImageEndPointActive = false,
): Promise<{ deliveryUrl?: string; assetId?: string; reason?: any; indexFile?: number }[]> {
  const uploadableFiles = files.filter((file) =>
    isImageUploadableForNewEndpoint((file?.image?.content as NewImage).file),
  );

  const uploadPromises = uploadableFiles.map((file) => {
    const actualFile = (file.image?.content as NewImage).file;
    const originalFile = (file.image?.content as NewImage)?.originalFile;
    return uploadNewImage(actualFile, file.index, storeId, lang, originalFile, isOriginalImageEndPointActive);
  });

  return Promise.all(uploadPromises);
}
